import { SetStateAction, useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CatRunRef, IntegrationStatusResult } from "../../../client/types.gen";
import { catRunStatuses, type RunType } from "../../../api/routes";

export const POLL_INTERVAL_MS = 2000;

export interface CatRunStatusPolling {
  pendingIds: (type: RunType) => string[];
  setPendingIds: (type: RunType, action: SetStateAction<string[]>) => void;
  /** Undefined until the server has a status for this pending run. */
  statusOf: (
    type: RunType,
    requestId: string
  ) => IntegrationStatusResult | undefined;
}

interface PollState {
  runs: CatRunRef[];
  statuses: Record<string, IntegrationStatusResult>;
}

const runKey = (type: RunType, requestId: string) => `${type}:${requestId}`;

const idsOf = (runs: CatRunRef[], type: RunType) =>
  runs.filter((r) => r.type === type).map((r) => r.request_id);

/** Polls every pending CAT and rater run in one request per interval. */
export function useCatRunStatusPolling(): CatRunStatusPolling {
  const [state, setState] = useState<PollState>({ runs: [], statuses: {} });

  const incomplete = state.runs.filter(
    (r) => !state.statuses[runKey(r.type, r.request_id)]?.is_complete
  );

  const { data } = useQuery({
    queryKey: [
      "cat-status",
      incomplete.map((r) => runKey(r.type, r.request_id)),
    ],
    queryFn: () => catRunStatuses(incomplete),
    enabled: incomplete.length > 0,
    refetchInterval: POLL_INTERVAL_MS,
    // A resumed run is polled again under its old id. A cached response
    // would report its previous completion straight away.
    gcTime: 0,
  });

  useEffect(() => {
    if (!data) return;
    setState((prev) => {
      const pending = new Set(
        prev.runs.map((r) => runKey(r.type, r.request_id))
      );
      const statuses = { ...prev.statuses };
      for (const run of data.runs) {
        const key = runKey(run.type, run.request_id);
        // A null status means the run has no tasks yet, so it stays pending.
        if (run.status && pending.has(key)) statuses[key] = run.status;
      }
      return { ...prev, statuses };
    });
  }, [data]);

  const setPendingIds = useCallback(
    (type: RunType, action: SetStateAction<string[]>) => {
      setState((prev) => {
        const prevIds = idsOf(prev.runs, type);
        const nextIds = typeof action === "function" ? action(prevIds) : action;
        const runs = [
          ...prev.runs.filter((r) => r.type !== type),
          ...nextIds.map((request_id) => ({ type, request_id })),
        ];
        // Keep a status only for runs that stay pending, so a re-added id
        // starts over instead of reading as already complete.
        const kept = new Set(
          prevIds
            .filter((id) => nextIds.includes(id))
            .map((id) => runKey(type, id))
        );
        const statuses = Object.fromEntries(
          Object.entries(prev.statuses).filter(
            ([key]) => !key.startsWith(`${type}:`) || kept.has(key)
          )
        );
        return { runs, statuses };
      });
    },
    []
  );

  return {
    pendingIds: (type) => idsOf(state.runs, type),
    setPendingIds,
    statusOf: (type, requestId) => state.statuses[runKey(type, requestId)],
  };
}
